import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Status } from "src/core/constants/folders";
import { CustomRiskLevel } from "src/core/constants/risk";
import { Roles } from "src/core/constants/roles";
import { TabCounters, useFolder } from "src/core/contexts/FolderContext";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { usePermissions } from "src/core/hooks/usePermissions";
import { mapFolderFileQueryToHeaderModel } from "src/core/mappers/folderHeader.mapper";
import { Contact, Owner } from "src/core/types/contact";

type FolderHeaderVm = {
  name: string;
  isPerson: boolean;
  contactPerson: Contact | null;
  owner: Owner | null;
  folderRisks: { global: CustomRiskLevel };
  folderState: Status | undefined;
  customerFolderId: string | undefined;
  globalRisk: CustomRiskLevel;
  isRiskForced: boolean;
  finishedDate: string | null;
  tabCounters: TabCounters;
  displayComments: boolean;
  canEditContact: boolean;
  canFollow: boolean;
};

type FolderHeaderApi = {
  open: boolean;
  setOpen: (open: boolean) => void;
  refetch: () => void;
  goToHome: () => void;
};

export const useFolderHeader: () => { vm: FolderHeaderVm; api: FolderHeaderApi } = () => {
  const paths = useRequiredTenantPaths();
  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false);
  const { hasRole } = usePermissions();
  const { folderFile, tabCounters, refetch } = useFolder();

  const headerModel = useMemo(() => mapFolderFileQueryToHeaderModel(folderFile), [folderFile]);

  const goToHome = useCallback(() => {
    navigate(paths.home());
  }, [navigate, paths]);

  const vm = useMemo<FolderHeaderVm>(
    () => ({
      name: headerModel.name,
      contactPerson: headerModel.contactPerson,
      owner: headerModel.owner,
      folderRisks: headerModel.folderRisks,
      folderState: headerModel.state,
      customerFolderId: headerModel.customerFolderId,
      globalRisk: headerModel.globalRisk,
      isRiskForced: headerModel.isRiskForced,
      finishedDate: headerModel.finishedDate,
      tabCounters,
      displayComments: headerModel.hasComments,
      canEditContact: !hasRole(Roles.AUDITEUR),
      canFollow: hasRole(Roles.COLLABORATEUR),
      isPerson: headerModel.isPerson,
    }),
    [headerModel, tabCounters, hasRole]
  );

  const api = useMemo<FolderHeaderApi>(
    () => ({
      open,
      setOpen,
      refetch,
      goToHome,
    }),
    [open, setOpen, refetch, goToHome]
  );

  return { vm, api } as const;
};
